// src/pages/CategoryPage.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { Category, Product, ProductVariant } from '../types';
import ProductCard from '../components/ProductCard';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

interface CategoryWithCount extends Category {
    productCount: number;
    children?: CategoryWithCount[];
}

interface ProductWithVariants extends Product {
    variants: ProductVariant[];
}

export const CategoryPage: React.FC = () => {
    const navigate = useNavigate();
    const { categoryId } = useParams<{ categoryId?: string }>();
    const [category, setCategory] = useState<CategoryWithCount | null>(null);
    const [subcategories, setSubcategories] = useState<CategoryWithCount[]>([]);
    const [products, setProducts] = useState<ProductWithVariants[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [breadcrumbs, setBreadcrumbs] = useState<Category[]>([]);

    useEffect(() => {
        setCategory(null);
        setSubcategories([]);
        setProducts([]);
        setBreadcrumbs([]);
        fetchCategoryData();
    }, [categoryId]);

    // Рекурсивная функция для получения всех ID подкатегорий
    const getAllSubcategoryIds = async (parentId: number): Promise<number[]> => {
        const { data: subcategories } = await supabase
            .from('categories')
            .select('id')
            .eq('parent_id', parentId);

        let allIds = [parentId];

        if (subcategories && subcategories.length > 0) {
            for (const subcat of subcategories) {
                const childIds = await getAllSubcategoryIds(subcat.id);
                allIds = [...allIds, ...childIds];
            }
        }

        return allIds;
    };

    // Получение количества товаров для категории (включая подкатегории)
    const getProductCountForCategory = async (categoryId: number): Promise<number> => {
        const allCategoryIds = await getAllSubcategoryIds(categoryId);

        const { count, error } = await supabase
            .from('product_categories')
            .select('product_id', { count: 'exact', head: true })
            .in('category_id', allCategoryIds);

        if (error) {
            console.error('Error counting products:', error);
            return 0;
        }

        return count || 0;
    };

    const fetchCategoryData = async () => {
        try {
            setLoading(true);

            // 1. Текущая категория
            let currentCategory: CategoryWithCount | null = null;
            if (categoryId) {
                const { data: catData, error: catError } = await supabase
                    .from('categories')
                    .select('*')
                    .eq('id', parseInt(categoryId))
                    .single();

                if (catError && catError.code !== 'PGRST116') {
                    console.error('Error fetching current category:', catError);
                }
                if (catData) {
                    const productCount = await getProductCountForCategory(catData.id);
                    currentCategory = { ...catData, productCount };
                }
            }

            // 2. Подкатегории
            let query = supabase
                .from('categories')
                .select('*')
                .eq('is_active', true)
                .order('name');

            if (categoryId) {
                query = query.eq('parent_id', parseInt(categoryId));
            } else {
                query = query.is('parent_id', null);
            }

            const { data: subcats, error: subError } = await query;

            if (subError) {
                console.error('Error fetching subcategories:', subError);
                setSubcategories([]);
                setCategory(currentCategory);
                setLoading(false);
                return;
            }

            const subcatList = (subcats || []) as CategoryWithCount[];

            // 3. Количество товаров для каждой подкатегории (рекурсивно)
            for (const cat of subcatList) {
                cat.productCount = await getProductCountForCategory(cat.id);
            }

            setSubcategories(subcatList);
            setCategory(currentCategory);

            // 4. Хлебные крошки
            if (currentCategory) {
                await fetchBreadcrumbs(currentCategory.id);
            }

            // 5. Товары — только если нет подкатегорий
            if (subcatList.length === 0) {
                await fetchProductsForCategory(currentCategory?.id ?? null);
            }

        } catch (error) {
            console.error('Error fetching category data:', error);
            toast.error('Ошибка загрузки категорий');
        } finally {
            setLoading(false);
        }
    };

    const fetchProductsForCategory = async (catId: number | null) => {
        try {
            setLoadingProducts(true);

            let productsData: ProductWithVariants[] = [];
            let productIds: number[] = [];

            if (catId) {
                // Получаем все ID категории и подкатегорий
                const allCategoryIds = await getAllSubcategoryIds(catId);

                // Получаем ID товаров из всех категорий
                const { data: productCategories, error: pcError } = await supabase
                    .from('product_categories')
                    .select('product_id')
                    .in('category_id', allCategoryIds);

                if (pcError) {
                    console.error('Error fetching product categories:', pcError);
                    setProducts([]);
                    return;
                }

                if (!productCategories || productCategories.length === 0) {
                    setProducts([]);
                    return;
                }

                productIds = Array.from(new Set(productCategories.map(pc => pc.product_id)));
            }

            let query = supabase
                .from('products')
                .select('*, variants:product_variants(*)')
                .eq('status', 'approved')
                .order('created_at', { ascending: false });

            if (productIds.length > 0) {
                query = query.in('id', productIds);
            }

            const { data, error } = await query;

            if (error) {
                if (error.message?.includes('product_variants') || error.code === '42P01') {
                    console.warn('product_variants table not found, loading without variants');
                    let queryNoVariants = supabase
                        .from('products')
                        .select('*')
                        .eq('status', 'approved')
                        .order('created_at', { ascending: false });

                    if (productIds.length > 0) {
                        queryNoVariants = queryNoVariants.in('id', productIds);
                    }

                    const { data: dataNoVariants, error: err2 } = await queryNoVariants;
                    if (err2) throw err2;
                    productsData = (dataNoVariants || []).map(p => ({ ...p, variants: [] }));
                } else {
                    throw error;
                }
            } else {
                productsData = (data || []) as ProductWithVariants[];
            }

            setProducts(productsData);
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Ошибка загрузки товаров');
        } finally {
            setLoadingProducts(false);
        }
    };

    const fetchBreadcrumbs = async (catId: number) => {
        const breadcrumbList: Category[] = [];
        let currentId: number | null = catId;

        let safetyCounter = 0;
        while (currentId && safetyCounter < 10) {
            safetyCounter++;
            const { data: categoryData, error } = await supabase
                .from('categories')
                .select('*')
                .eq('id', currentId)
                .single();

            if (error) {
                console.error('Error fetching category for breadcrumb:', error);
                break;
            }

            if (!categoryData) break;

            // Явно указываем тип при присваивании
            const typedCategory: Category = {
                id: categoryData.id,
                name: categoryData.name,
                description: categoryData.description,
                image_url: categoryData.image_url,
                parent_id: categoryData.parent_id,
                level: categoryData.level,
                is_active: categoryData.is_active,
                created_at: categoryData.created_at
            };

            breadcrumbList.unshift(typedCategory);
            currentId = typedCategory.parent_id ?? null;
        }

        setBreadcrumbs(breadcrumbList);
    };

    const handleCategoryClick = (cat: CategoryWithCount) => {
        navigate(`/category/${cat.id}`);
    };

    const handleBack = () => {
        if (breadcrumbs.length > 1) {
            navigate(`/category/${breadcrumbs[breadcrumbs.length - 2].id}`);
        } else {
            navigate('/');
        }
    };

    if (loading) {
        return (
            <div className="loader-container">
                <div className="spinner"></div>
                <p style={{ marginTop: '16px', color: '#6b7280' }}>Загрузка...</p>
            </div>
        );
    }

    const BreadcrumbsBar = () => (
        <div className="breadcrumbs">
            <button onClick={() => navigate('/')} className="breadcrumb-link">
                Главная
            </button>
            {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={crumb.id}>
                    <span className="breadcrumb-separator">/</span>
                    {index === breadcrumbs.length - 1 ? (
                        <span className="breadcrumb-current">{crumb.name}</span>
                    ) : (
                        <button
                            onClick={() => navigate(`/category/${crumb.id}`)}
                            className="breadcrumb-link"
                        >
                            {crumb.name}
                        </button>
                    )}
                </React.Fragment>
            ))}
        </div>
    );

    const CategoryHeader = () => (
        <div className="category-header">
            <button onClick={handleBack} className="back-button-category">
                <ArrowLeftIcon style={{ width: 20, height: 20 }} />
                <span>Назад</span>
            </button>
            <h1 className="category-title">
                {category ? category.name : (categoryId ? 'Категория' : 'Все категории')}
            </h1>
            {category?.description && (
                <p className="category-description">{category.description}</p>
            )}
        </div>
    );

    // Показываем подкатегории
    if (subcategories.length > 0) {
        return (
            <div className="category-page">
                <BreadcrumbsBar />
                <CategoryHeader />

                <div className="subcategories-grid">
                    {subcategories.map((subcat) => (
                        <div
                            key={subcat.id}
                            onClick={() => handleCategoryClick(subcat)}
                            className="subcategory-card"
                        >
                            <div className="subcategory-image">
                                <img
                                    src={subcat.image_url || `https://via.placeholder.com/400x300/f3f4f6/9ca3af?text=${encodeURIComponent(subcat.name)}`}
                                    alt={subcat.name}
                                    className="subcategory-img"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src =
                                            `https://via.placeholder.com/400x300/f3f4f6/9ca3af?text=${encodeURIComponent(subcat.name)}`;
                                    }}
                                />
                            </div>
                            <div className="subcategory-info">
                                <h3 className="subcategory-name">{subcat.name}</h3>
                                {subcat.productCount > 0 && (
                                    <p className="subcategory-count">{subcat.productCount} товаров</p>
                                )}
                            </div>
                            <div className="subcategory-arrow">→</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Показываем товары
    return (
        <div className="category-page">
            <BreadcrumbsBar />
            <CategoryHeader />

            {loadingProducts ? (
                <div className="loader-container">
                    <div className="spinner"></div>
                    <p style={{ marginTop: '16px', color: '#6b7280' }}>Загрузка товаров...</p>
                </div>
            ) : products.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">📦</div>
                    <h3>Товары не найдены</h3>
                    <p>В этой категории пока нет товаров</p>
                </div>
            ) : (
                <div className="products-list">
                    {products.map((product) => (
                        <ProductCard key={product.id} product={product} />
                    ))}
                </div>
            )}
        </div>
    );
};